import React, { useEffect, useRef, useState } from 'react';

// Prime-React Components
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';

// Utils
// Hide Export Functionality
// import { saveAsExcelFile } from '../utils/excel';

// Constants
import { PAGINATION_OPTIONS } from '../constants/dropdownOptions';
import { getUserTableName, isValidExperienceFormat } from '../utils/common';
import { CustomeTooltip } from './ActionButtons';
import { MultiSelect } from 'primereact/multiselect';
import { Menu } from '@mui/material';
import { EXPERIENCE_FORMAT_ERROR_MESSAGE, EXPERIENCE_FORMAT_EXAMPLE } from '../constants/common';
import CustomButton from './CustomButton';

const CustomDataTable = (props) => {
  const tableRef = useRef(null);
  const tableData = props.data;
  // Hide Export Functionality
  // const tableExportedData = props.exportData;
  const tableColumns = props.columns;
  const tableTotalRecords = props.totalRecords;
  const tableName = props.name;
  const [tableLazyParams, setTableLazyParams] = useState({
    first: 0,
    page: 0
  });
  // const [selectedLimit, setSelectedLimit] = useState(null);
  const [minExp, setMinExp] = useState(props.minExp);
  const [maxExp, setMaxExp] = useState(props.maxExp);
  const [search, setSearch] = useState(props.searchValue || '');
  const isInputFocused = useRef(false);
  const lastSearchSent = useRef(props.searchValue);
  const minInputRef = useRef(null);
  const maxInputRef = useRef(null);
  const clearBtnRef = useRef(null);
  const filterBtnRef = useRef(null);

  useEffect(() => {
    setMinExp(props.minExp ?? null);
  }, [props.minExp]);

  useEffect(() => {
    setMaxExp(props.maxExp ?? null);
  }, [props.maxExp]);

  useEffect(() => {
    if (!isInputFocused.current || !props.searchValue) {
      setSearch(props.searchValue || '');
      lastSearchSent.current = props.searchValue;
    }
  }, [props.searchValue]);

  // Hide Export Functionality
  // const exportExcel = () => {
  //   const exportFields = tableColumns
  //     .filter((item) => item.isExport !== false)
  //     .map((column) => column.field);
  //   const exportData = tableExportedData.map((dataObj) => {
  //     const filteredObj = {};
  //     exportFields.forEach((fieldName) => (filteredObj[fieldName] = dataObj[fieldName]));
  //     return filteredObj;
  //   });
  //   saveAsExcelFile(tableName, exportData);
  // };

  const tableHeader = (
    <div className="table-header flex justify-content-between">
      <div className="table-actions-left flex-wrap">
        {props.onBack && (
          <div className="table-actions-right flex">
            <Button data-tip={'Back'} type="button" className="add-user-btn" onClick={props.onBack}>
              <i className="pi pi-fw pi-arrow-left" />
            </Button>
          </div>
        )}
        <div className="input-outer-wrapper table-actions-dropdowns filter-margin">
          {props.onSearch && (
            <div className="dropdowns-inner-wrapper">
              <div className="p-input-icon-left search-input">
                <i className="pi pi-search" />
                <InputText
                  type="search"
                  value={search}
                  onFocus={() => {
                    isInputFocused.current = true;
                  }}
                  onBlur={() => {
                    isInputFocused.current = false;
                  }}
                  onInput={(e) => {
                    const val = e.target.value;
                    lastSearchSent.current = val;
                    setSearch(val);
                    if (props.onSearch) {
                      props.onSearch(val);
                    }
                  }}
                  placeholder={props.searchPlaceHolder || 'Search'}
                />
              </div>
            </div>
          )}
        </div>
        {props.experienceFilter && (
          <div className="input-outer-wrapper table-actions-dropdowns filter-margin">
            <div className="dropdowns-inner-wrapper">
              <div
                className={`custom-form-group experienceFilter status-select-dropdown ${
                  (props.minExp !== null && props.minExp !== undefined) ||
                  (props.maxExp !== null && props.maxExp !== undefined)
                    ? 'has-value'
                    : ''
                }`}>
                <Button
                  tabIndex={0}
                  role="button"
                  variant="outlined"
                  aria-controls={props.open ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={props.open ? 'true' : undefined}
                  onClick={props.onExperienceFilter}>
                  {(props.minExp !== null && props.minExp !== undefined) ||
                  (props.maxExp !== null && props.maxExp !== undefined)
                    ? `${props.minExp || 0} - ${props.maxExp || 100} Exp`
                    : props.label}
                  <span className="pi pi-chevron-down"></span>
                </Button>
              </div>
            </div>
          </div>
        )}
        {props.hrFilter && (
          <div className="table-actions-dropdowns filter-margin">
            <div className="dropdowns-inner-wrapper">
              <div className="custom-form-group status-select-dropdown">
                <MultiSelect
                  key={props.hrs.length}
                  filter
                  optionLabel="name"
                  optionValue="id"
                  maxSelectedLabels={2}
                  value={props.selectedHrs}
                  options={props.hrs}
                  display="chip"
                  onChange={(e) => props.onFilter(e.value)}
                  placeholder="Select HRs"
                  itemTemplate={(option) => (
                    <div
                      className={`flex align-items-center ${
                        !option.isActive ? 'inactive-hr-item' : ''
                      }`}
                      style={{ position: 'relative', paddingRight: '14px', width: '100%' }}>
                      {option.name}
                      <span
                        className={`hr-status-dot ${
                          option.isActive ? 'active-dot' : 'inactive-dot'
                        }`}
                        style={{ position: 'absolute', right: 0, top: '5px' }}></span>
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        )}
        {props.dropdown && (
          <div className="table-actions-dropdowns filter-margin">
            <div className="dropdowns-inner-wrapper">
              <div className="custom-form-group status-select-dropdown">
                {props.isDropdownMultiSelect ? (
                  <MultiSelect
                    key={props.dropdown.length}
                    filter
                    value={props?.dropdownValue}
                    className="custom-dropdown"
                    options={props.dropdown}
                    panelClassName="custom-dropdown-panel"
                    onChange={(e) => {
                      props.onStatusFilter && props.onStatusFilter(e.value || '');
                    }}
                    maxSelectedLabels={2}
                    display="chip"
                    optionLabel="name"
                    optionValue="value"
                    {...(props.isdropdownGroup && { optionGroupLabel: 'label' })}
                    {...(props.isdropdownGroup && { optionGroupChildren: 'items' })}
                    placeholder={props.dropdownPlaceHolder || 'Status'}
                  />
                ) : (
                  <Dropdown
                    value={props?.dropdownValue}
                    className="custom-dropdown"
                    options={props.dropdown}
                    panelClassName="custom-dropdown-panel"
                    onChange={(e) => {
                      props.onStatusFilter && props.onStatusFilter(e.value || null);
                    }}
                    showClear
                    optionLabel="name"
                    placeholder={props.dropdownPlaceHolder || 'Status'}
                  />
                )}
              </div>
            </div>
          </div>
        )}
        {props.secondDropdown && (
          <div className="table-actions-dropdowns filter-margin">
            <div className="dropdowns-inner-wrapper">
              <div className="custom-form-group status-select-dropdown">
                {props.isSecondDropdownMultiSelect ? (
                  <MultiSelect
                    key={props.secondDropdown.length}
                    value={props?.secondDropdownValue}
                    className="custom-dropdown"
                    options={props.secondDropdown}
                    panelClassName="custom-dropdown-panel"
                    onChange={(e) => {
                      props.onSecondDropdownChange && props.onSecondDropdownChange(e.value || '');
                    }}
                    maxSelectedLabels={2}
                    display="chip"
                    optionLabel="name"
                    optionValue="value"
                    placeholder={props.secondDropdownPlaceHolder || 'Select...'}
                  />
                ) : (
                  <Dropdown
                    value={props?.secondDropdownValue}
                    className="custom-dropdown"
                    options={props.secondDropdown}
                    panelClassName="custom-dropdown-panel"
                    onChange={(e) => {
                      props.onSecondDropdownChange && props.onSecondDropdownChange(e.value || null);
                    }}
                    showClear
                    optionLabel="name"
                    placeholder={props.secondDropdownPlaceHolder || 'Select Duration'}
                  />
                )}
              </div>
            </div>
          </div>
        )}
        {props.thirdDropdown && (
          <div className="table-actions-dropdowns">
            <div className="dropdowns-inner-wrapper">
              <div className="custom-form-group status-select-dropdown">
                {props.isThirdDropdownMultiSelect ? (
                  <MultiSelect
                    key={props.thirdDropdown.length}
                    value={props?.thirdDropdownValue}
                    className="custom-dropdown"
                    options={props.thirdDropdown}
                    panelClassName="custom-dropdown-panel"
                    onChange={(e) => {
                      props.onThirdDropdownChange && props.onThirdDropdownChange(e.value || '');
                    }}
                    maxSelectedLabels={2}
                    display="chip"
                    optionLabel="name"
                    optionValue="value"
                    placeholder={props.thirdDropdownPlaceHolder || 'Select...'}
                  />
                ) : (
                  <Dropdown
                    value={props?.thirdDropdownValue}
                    className="custom-dropdown"
                    options={props.thirdDropdown}
                    panelClassName="custom-dropdown-panel"
                    onChange={(e) => {
                      props.onThirdDropdownChange && props.onThirdDropdownChange(e.value || null);
                    }}
                    showClear
                    optionLabel="name"
                    placeholder={props.thirdDropdownPlaceHolder || 'Select...'}
                  />
                )}
              </div>
            </div>
          </div>
        )}
        {props.clearFilter && (
          <div className="table-actions-dropdowns">
            <div className="dropdowns-inner-wrapper">
              <div className="custom-form-group status-select-dropdown">
                <Button
                  variant="outlined"
                  aria-haspopup="true"
                  disabled={props.isClearFilterDisabled}
                  onClick={() => {
                    setSearch('');
                    props.clearFilterOnClick();
                  }}>
                  Clear Filter
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="table-actions-right flex">
        {/* Hide Export Functionality */}
        {/* {tableData.length > 0 && (
          <Button type="button" className="export-btn" onClick={exportExcel}>
            <i className="pi pi-fw pi-download" />
            EXPORT
          </Button>
        )} */}
        {props.onAdd && (
          <>
            <CustomeTooltip place="left"></CustomeTooltip>
            <Button
              data-tip={getUserTableName(tableName)}
              type="button"
              className="add-user-btn"
              onClick={props.onAdd}>
              <i className="pi pi-fw pi-plus" />
            </Button>
          </>
        )}
        {props.onCancel && (
          <>
            <CustomeTooltip place="left"></CustomeTooltip>
            <span data-tip={props.CancelTooltip || 'Cancel'}>
              <Button
                type="button"
                className="add-user-btn"
                disabled={props.cancelDisabled}
                onClick={props.onCancel}>
                <i className="pi pi-fw pi-times" />
              </Button>
            </span>
          </>
        )}
      </div>
    </div>
  );

  // const onPage = (e) => {
  //   setTableLazyParams(e);
  //   const rowsPerPage = e.rows;
  //   setSelectedLimit(rowsPerPage);
  //   const pageNumber = e.page + 1;
  //   props.onPagination(pageNumber, rowsPerPage);
  // };

  const onPage = (e) => {
    setTableLazyParams(e);

    const isSizeChanged = e.rows !== props.pageSize;
    const pageNumber = isSizeChanged ? props.pageNumber : e.page + 1;

    props.onPagination(pageNumber, e.rows);
  };

  const onSort = (e) => {
    const fieldName = e.multiSortMeta[0].field;
    const sort = tableColumns.find((column) => column.field === fieldName);
    props.onSort(sort.sortBy);
  };

  useEffect(() => {
    if (props.pageNumber && props.pageSize) {
      setTableLazyParams({
        first: (props.pageNumber - 1) * props.pageSize,
        page: props.pageNumber - 1
      });
    } else {
      setTableLazyParams({
        first: 0,
        page: 0
      });
    }
  }, [props.pageNumber, props.pageSize]);

  return (
    <div className="white-box custom-table-wrapper p-0 overflow-hidden">
      <DataTable
        {...props}
        size="small"
        ref={tableRef}
        value={tableData}
        header={!props?.isHeaderDisable ? tableHeader : null}
        totalRecords={tableTotalRecords}
        paginator={true && !props.disablePaginator}
        // rows={selectedLimit ? selectedLimit : 10}
        rows={props.pageSize}
        lazy={props.lazy !== undefined ? props.lazy : true}
        first={tableLazyParams.first || 0}
        onPage={onPage}
        onSort={onSort}
        showGridlines
        stripedRows
        responsive={true}
        rowsPerPageOptions={props.rowPerPageOption ? props.rowPerPageOption : PAGINATION_OPTIONS}
        // selection={selectedLimit}
        // onSelectionChange={(e) => setSelectedLimit(e.rows)}
        paginatorTemplate="CurrentPageReport RowsPerPageDropdown PrevPageLink PageLinks NextPageLink"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        sortMode="multiple"
        responsiveLayout="scroll">
        {tableColumns?.map((column) => {
          return (
            <Column
              sortable={column.sortBy ? true : false}
              key={column.field}
              field={column.field}
              header={column.header}
              className={column.class}
              {...(column.width && { style: { width: column.width } })}
              {...(column.expander && { expander: column.expander })}
              {...(column.body && { body: column.body })}
            />
          );
        })}
      </DataTable>

      <Menu
        id="basic-menu"
        anchorEl={props.anchorEl}
        open={props.open || false}
        className="experience-filter-dropdown"
        onClose={() => {
          props.handleClose();
        }}
        disableAutoFocus
        disableEnforceFocus
        MenuListProps={{
          onKeyDown: (e) => {
            if (e.key === 'Escape') {
              props.handleClose();
            }
          }
        }}
        TransitionProps={{
          onEntered: () => {
            requestAnimationFrame(() => {
              const minInput = document.querySelector('#min-exp-input');
              minInput?.focus();
            });
          }
        }}>
        <div className="candidate-experience-filter" tabIndex={-1} aria-hidden="true">
          {EXPERIENCE_FORMAT_EXAMPLE}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', padding: '8px 16px' }}>
          <InputNumber
            ref={minInputRef}
            inputId="min-exp-input"
            id={minExp}
            value={minExp}
            className="ud-status-dropdown"
            inputClassName="dark-input-text"
            style={{
              width: 75,
              marginRight: 10
            }}
            onValueChange={(e) => {
              if (e.value === null || /^\d*\.?\d*$/.test(e.value)) {
                setMinExp(e.value);
              }
            }}
            mode="decimal"
            minFractionDigits={2}
            maxFractionDigits={2}
            min={0}
            max={100}
            allowEmpty
          />{' '}
          <span className="exp-separator">To</span>
          <InputNumber
            ref={maxInputRef}
            inputId="max-exp-input"
            id={maxExp}
            value={maxExp}
            className="ud-status-dropdown"
            inputClassName="dark-input-text"
            style={{
              width: 75,
              marginRight: 10,
              marginLeft: 10
            }}
            onValueChange={(e) => {
              if (e.value === null || /^\d*\.?\d*$/.test(e.value)) {
                setMaxExp(e.value);
              }
            }}
            mode="decimal"
            minFractionDigits={2}
            maxFractionDigits={2}
            min={0}
            max={100}
            allowEmpty
          />
          <CustomButton
            type="button"
            variant="contained"
            ref={filterBtnRef}
            className="mr-2"
            // disabled={
            //   !(
            //     minExp !== null &&
            //     maxExp !== null &&
            //     Number(maxExp) >= Number(minExp) &&
            //     isValidExperienceFormat(minExp) &&
            //     isValidExperienceFormat(maxExp)
            //   )
            // }
            onClick={() => {
              props.handleYearExperienceFilter(minExp, maxExp);
              props?.handleClose();
            }}>
            Filter
          </CustomButton>
          <CustomButton
            type="button"
            className="gray-btn"
            color="error"
            variant="contained"
            style={{ marginRight: 8 }}
            ref={clearBtnRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                props.handleYearExperienceFilter(null, null);
                setMinExp(null);
                setMaxExp(null);
                props?.handleClose();
              }
            }}
            onClick={() => {
              setMinExp(null);
              setMaxExp(null);
              props.handleYearExperienceFilter(null, null);
            }}>
            Clear
          </CustomButton>
        </div>
        {(!isValidExperienceFormat(minExp) || !isValidExperienceFormat(maxExp)) && (
          <small className="p-error candidate-experience-filter">
            {EXPERIENCE_FORMAT_ERROR_MESSAGE}
          </small>
        )}
      </Menu>
    </div>
  );
};

CustomDataTable.defaultProps = {
  onAdd: null,
  onSort: null,
  onPagination: null,
  onSearch: null,
  pagination: null,
  dropdown: null,
  columns: [],
  data: [],
  exportData: [],
  name: null,
  hrFilter: false,
  totalRecords: 0
};

export default CustomDataTable;
